import Configuration from '../constant/configuration';

/**
 * Add Device routing for Pro Wi-Fi Provisioning.
 *
 * iOS 26 Local Network Privacy blocks browsers from reaching the Pro SoftAP,
 * so the Wi-Fi step moved into the native Kegtron app. Tapping "Add Device"
 * now routes by platform:
 *   - Desktop  → existing legacy browser flow (dash.kegtron.com)
 *   - iOS      → launch the app via custom scheme, fall back to the App Store
 *                via a JS timer (the custom scheme has no native fallback)
 *   - Android  → launch the app via an intent:// URL whose embedded
 *                browser_fallback_url sends app-less users to the Play Store
 *                (Chrome/Samsung Internet handle that fallback natively, so no
 *                JS timer is needed). Validated on-device 2026-06-27.
 *
 * Platform detection and launch logic are centralized here so they're testable
 * without a real browser (all DOM access is injectable via `deps`).
 */

export const PLATFORM = Object.freeze({
  IOS: 'ios',
  ANDROID: 'android',
  DESKTOP: 'desktop',
});

// How long to wait for the app to take over before sending the user to the
// store. If the page goes hidden within this window the app opened, so we
// don't bounce the user to the store.
const APP_LAUNCH_FALLBACK_MS = 1200;

/**
 * Detect the platform from a navigator-like object.
 * @param {object} nav - navigator-like object (defaults to global navigator)
 * @returns {'ios'|'android'|'desktop'}
 */
export function detectPlatform(nav) {
  const n = nav || (typeof navigator !== 'undefined' ? navigator : {});
  const ua = n.userAgent || '';
  const platform = n.platform || '';
  const maxTouchPoints = n.maxTouchPoints || 0;

  if (/android/i.test(ua)) return PLATFORM.ANDROID;

  // iPhone/iPod/iPad report directly; iPadOS 13+ reports as "MacIntel" with
  // a touch screen, so disambiguate via maxTouchPoints.
  const isIosUa = /iphone|ipod|ipad/i.test(ua);
  const isIpadOsAsMac = /mac/i.test(platform) && maxTouchPoints > 1;
  if (isIosUa || isIpadOsAsMac) return PLATFORM.IOS;

  return PLATFORM.DESKTOP;
}

/**
 * Build the Android intent:// launch URL for Pro Wi-Fi Provisioning. Opens the
 * native app via the same `kegtron://provision/pro` scheme used on iOS, pinned
 * to the Kegtron package (so Chrome skips the app-chooser dialog), with a
 * URL-encoded Play Store browser_fallback_url for devices that don't have the
 * app installed. Scheme + path are derived from Configuration.appScheme so iOS
 * and Android stay in lockstep.
 * @returns {string}
 */
export function buildAndroidIntentUrl() {
  const [scheme, path = ''] = String(Configuration.appScheme).split('://');
  return (
    `intent://${path}#Intent;` +
    `scheme=${scheme};` +
    `package=${Configuration.androidPackage};` +
    `S.browser_fallback_url=${encodeURIComponent(Configuration.androidPlayStoreUrl)};` +
    'end'
  );
}

/**
 * Pure descriptor of what "Add Device" should do for a given platform.
 * @param {'ios'|'android'|'desktop'} platform
 * @returns {{ mode: 'app'|'legacy', launchUrl: ?string, storeUrl: ?string,
 *             useNativeFallback: boolean, allowBrowserFallback: boolean }}
 */
export function getAddDeviceTarget(platform) {
  switch (platform) {
    case PLATFORM.IOS:
      return {
        mode: 'app',
        launchUrl: Configuration.appScheme,
        storeUrl: Configuration.iosAppStoreUrl,
        // Custom schemes have no native store fallback → time-box with a JS
        // timer (see launchSchemeThenStore).
        useNativeFallback: false,
        allowBrowserFallback: false,
      };
    case PLATFORM.ANDROID:
      return {
        mode: 'app',
        launchUrl: buildAndroidIntentUrl(),
        // The intent's embedded browser_fallback_url handles the no-app case,
        // so there's no separate JS store fallback to schedule.
        storeUrl: null,
        useNativeFallback: true,
        allowBrowserFallback: true,
      };
    case PLATFORM.DESKTOP:
    default:
      return {
        mode: 'legacy',
        launchUrl: null,
        storeUrl: null,
        useNativeFallback: false,
        allowBrowserFallback: true,
      };
  }
}

/**
 * Build the legacy browser Add Device URL. The access token rides the URL
 * fragment (never sent over the network) and is built at call time so it never
 * sits in the DOM (see footer security note / audit M3).
 * @param {string} ktok - mDash customer token
 * @param {string} lang - current language code
 * @returns {string}
 */
export function buildLegacyAddDeviceUrl(ktok, lang) {
  return `${Configuration.addDeviceBaseUrl}/#/add?access_token=${ktok}&lang=${lang}`;
}

// Treat an unfilled placeholder store URL (idXXXXXXXXX) as unusable so we don't
// navigate the user to a broken App Store page before the real ID is set.
function isUsableStoreUrl(url) {
  return !!url && !/XXXXXXX/.test(url);
}

/**
 * Attempt the custom-scheme launch; if the app doesn't take over within
 * APP_LAUNCH_FALLBACK_MS (page never goes hidden), redirect to the store.
 */
function launchSchemeThenStore({ win, doc, launchUrl, storeUrl }) {
  let appOpened = false;
  const markHidden = () => {
    if (doc.visibilityState === 'hidden') appOpened = true;
  };

  doc.addEventListener('visibilitychange', markHidden);
  win.addEventListener('pagehide', markHidden);

  win.location.href = launchUrl;

  win.setTimeout(() => {
    doc.removeEventListener('visibilitychange', markHidden);
    win.removeEventListener('pagehide', markHidden);
    // Still here and visible → the app didn't open → send to the store.
    // Guard against an unfilled placeholder store URL (see config TODOs):
    // navigating to it would dump the user on a broken store page.
    if (!appOpened && doc.visibilityState !== 'hidden' && isUsableStoreUrl(storeUrl)) {
      win.location.assign(storeUrl);
    }
  }, APP_LAUNCH_FALLBACK_MS);
}

/**
 * Perform the platform-appropriate Add Device action.
 *
 * @param {object}   params
 * @param {boolean}  params.isDemo            - demo mode short-circuits (no action)
 * @param {string}   params.lang              - current language code
 * @param {string}   params.ktok              - mDash customer token (legacy flow only)
 * @param {Function} [params.onBrowserFallback] - called with the legacy URL on
 *                     Android so the caller can render a de-emphasized
 *                     "continue in browser" affordance
 * @param {object}   [deps]                   - injectable { win, doc, platform } for tests
 */
export function launchAddDevice(
  { isDemo, lang, ktok, onBrowserFallback } = {},
  deps = {}
) {
  if (isDemo) return;

  const win = deps.win || window;
  const doc = deps.doc || document;
  const platform = deps.platform || detectPlatform(deps.nav);
  const target = getAddDeviceTarget(platform);

  if (target.mode === 'legacy') {
    win.location.assign(buildLegacyAddDeviceUrl(ktok, lang));
    return;
  }

  if (target.useNativeFallback) {
    // Android intent:// — Chrome/Samsung Internet launch the app or send the
    // user to the Play Store via the embedded browser_fallback_url, so we just
    // navigate; no JS store-fallback timer is needed.
    win.location.href = target.launchUrl;
  } else {
    // iOS custom scheme — no native fallback, so time-box the launch and bounce
    // to the store if the app never takes over.
    launchSchemeThenStore({ win, doc, launchUrl: target.launchUrl, storeUrl: target.storeUrl });
  }

  if (target.allowBrowserFallback && typeof onBrowserFallback === 'function') {
    onBrowserFallback(buildLegacyAddDeviceUrl(ktok, lang));
  }
}
